// react
import { useMemo } from 'react';

// internationalization
import { useTranslation } from 'react-i18next';

// material ui icons
import ApartmentRoundedIcon from '@mui/icons-material/ApartmentRounded';
import SettingsInputComponentRoundedIcon from '@mui/icons-material/SettingsInputComponentRounded';

// material ui
import {
  alpha,
  Box,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';

const RED = '#ff5a39';
const GREEN = '#96b100';

function getAccentSx(scope, type) {
  // Base: always show an 8px left accent
  const base = {
    boxShadow: 'none',
    mb: 2,
    borderLeftWidth: '8px',
    borderLeftStyle: 'solid',
  };

  // Guard: if we’re missing info, keep it neutral
  if (!scope || !type) {
    return {
      ...base,
      borderLeftColor: 'transparent',
    };
  }

  const isEmpty = String(type) === 'empty';
  const color = isEmpty ? RED : GREEN;

  if (scope === 'subscription') {
    // dashed/gradient look via border-image
    return {
      ...base,
      borderLeftColor: 'transparent',
      borderImage: `repeating-linear-gradient(45deg, ${color}, ${color} 4px, transparent 4px, transparent 8px) 1`,
      backgroundColor: alpha(color, 0.3),
      borderImageSlice: 1,
    };
  }

  if (scope === 'building') {
    // solid color for buildings
    return {
      ...base,
      borderLeftColor: color,
      backgroundColor: alpha(color, 0.3),
      borderImage: 'none',
      borderImageSlice: undefined,
    };
  }

  // Fallback
  return {
    ...base,
    borderLeftColor: 'transparent',
  };
}

export function EventDetails({ isEventClick, selectedEvent, subsById }) {
  const { t } = useTranslation('calendar');
  const hasSubs = (selectedEvent.affectedSubscriptions?.length ?? 0) > 0;

  // scope-based accent (uses selectedEvent.type for Empty/Occupied)
  const accentSx = useMemo(
    () => getAccentSx(selectedEvent.scope, selectedEvent.type),
    [selectedEvent.scope, selectedEvent.type],
  );
  if (!isEventClick) return null;

  return (
    <>
      <Card sx={accentSx}>
        <CardContent sx={{ py: 2.5 }}>
          <Stack
            direction="row"
            spacing={2}
            flexWrap="wrap"
            alignItems="center"
          >
            {/* Building scope */}
            {selectedEvent.scope === 'building' && (
              <Box sx={{ minWidth: 280 }}>
                <List
                  dense
                  sx={{
                    py: 0,
                    '& .MuiListItem-root': { px: 0.5 },
                    '& .MuiListItemIcon-root': { minWidth: 36 },
                  }}
                >
                  <ListItem disableGutters>
                    <ListItemIcon>
                      <ApartmentRoundedIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography fontWeight={600}>
                          {selectedEvent.buildingName ||
                            selectedEvent.buildingId}
                        </Typography>
                      }
                    />
                  </ListItem>

                  {hasSubs && (
                    <ListItem disableGutters sx={{ mt: 0.5, mb: -0.5 }}>
                      <ListItemText
                        primary={
                          <Typography variant="body2" color="text.secondary">
                            {t('affectedCircuits')}
                          </Typography>
                        }
                      />
                    </ListItem>
                  )}

                  {selectedEvent.affectedSubscriptions?.map((sid) => {
                    const s = subsById.get(sid);
                    const label = s?.DisplayName
                      ? `${s.DisplayName}`
                      : `Blandesløjfe ${sid}`;
                    return (
                      <ListItem key={String(sid)} disableGutters>
                        <ListItemIcon>
                          <SettingsInputComponentRoundedIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary={label} />
                      </ListItem>
                    );
                  })}
                </List>
              </Box>
            )}

            {/* Subscription scope */}
            {selectedEvent.scope === 'subscription' &&
              selectedEvent.subscriptionDisplayName && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    px: 1.5,
                    py: 1,
                  }}
                >
                  <SettingsInputComponentRoundedIcon
                    color="action"
                    fontSize="small"
                    sx={{ flexShrink: 0 }}
                  />
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 600, color: 'text.primary' }}
                  >
                    {t('circuit')} {selectedEvent.subscriptionDisplayName}
                  </Typography>
                </Box>
              )}
          </Stack>
        </CardContent>
      </Card>
    </>
  );
}

